import { RefreshCw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatAvatar } from './ChatAvatar';
import { CopyButton } from './CopyButton';
import { ThinkingIndicator } from './ThinkingIndicator';
import { ArtifactCard } from './ArtifactCard';
import { ArtifactStreamingBox } from '../../artifacts';
import { UIMessage, Artifact } from '../../../types/chat';

// Custom link renderer for ReactMarkdown
const CustomLink = (props: React.ComponentProps<'a'>) => {
  const { href, children } = props;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#F4CC04] hover:underline">
      {children}
    </a>
  );
};

interface ChatMessageProps {
  message: UIMessage;
  userName?: string;
  userInitial?: string;
  onRetry?: (message: UIMessage) => void;
  onViewArtifact?: (artifact: Artifact) => void;
  isLoading?: boolean;
  className?: string;
}

/**
 * ChatMessage - Componente de mensagem do chat
 *
 * Baseado no mockup:
 * - Mensagem do usuário: balão amarelo/dourado à direita
 * - Mensagem da TAIS: fundo escuro à esquerda
 */
export const ChatMessage = ({
  message,
  userName,
  userInitial,
  onRetry,
  onViewArtifact,
  isLoading = false,
  className = '',
}: ChatMessageProps) => {
  const isUser = message.sender === 'user';
  const isAI = message.sender === 'ai';
  const hasError = message.status === 'error';

  // Formatação de timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  if (isUser) {
    return (
      <div className={`flex justify-end gap-3 p-3 ${className}`}>
        <div className="flex flex-col items-end max-w-[80%]">
          {/* Nome e horário */}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[#888888] text-xs">
              {userName || 'Você'}
            </span>
            <span className="text-[#666666] text-xs">
              {formatTime(message.timestamp)}
            </span>
          </div>

          {/* Balão da mensagem */}
          <div
            className={`
              bg-[#F4CC04] text-black
              rounded-2xl
              px-4 py-3
              ${hasError ? 'opacity-70' : ''}
            `}
          >
            <p className="text-sm leading-relaxed whitespace-pre-wrap">
              {message.text}
            </p>

            {/* Arquivos anexados */}
            {message.files && message.files.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-black/10">
                {message.files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1.5 text-xs bg-black/10 px-2 py-1 rounded"
                  >
                    <span className="font-medium">{file.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Motivo da falha — sem isto o usuário só via o balão apagado */}
          {hasError && message.errorMessage && (
            <p className="text-red-400 text-xs mt-2 text-right">
              {message.errorMessage}
            </p>
          )}

          {/* Botão de retry em caso de erro */}
          {hasError && onRetry && (
            <button
              onClick={() => onRetry(message)}
              disabled={isLoading}
              className="
                flex items-center gap-1.5 mt-2
                text-[#888888] text-xs
                hover:text-white
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
              title={message.hadFiles ? 'Os arquivos anexados não serão reenviados' : 'Tentar reenviar mensagem'}
            >
              <RefreshCw size={14} />
              <span>Tentar novamente</span>
            </button>
          )}
        </div>

        {/* Avatar do usuário */}
        <ChatAvatar
          type="user"
          userName={userName}
          userInitial={userInitial}
        />
      </div>
    );
  }

  // Mensagem da TAIS (AI)
  return (
    <div className={`flex gap-3 p-3 ${className}`}>
      {/* Avatar TAIS */}
      <ChatAvatar type="ai" />

      <div className="flex-1 min-w-0">
        {/* Nome e horário */}
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[#F4CC04] text-xs font-medium">
            TAIS
          </span>
          <span className="text-[#666666] text-xs">
            {formatTime(message.timestamp)}
          </span>
        </div>

        {/* Conteúdo da mensagem */}
        <div className="bg-[#1a1a1a] border border-[#333333] rounded-2xl rounded-tl-sm p-4">
          {/* Indicador de pensando — nunca em cima de uma falha, senão fica
              girando pra sempre numa resposta que nunca vai chegar */}
          {isAI && !hasError && !message.text && !message.streamingArtifact && (
            <ThinkingIndicator />
          )}

          {/* Falha ao gerar a resposta */}
          {isAI && hasError && (
            <p className="text-red-400 text-sm">
              {message.errorMessage || 'Não foi possível gerar a resposta.'}
            </p>
          )}

          {/* Texto da mensagem com Markdown */}
          {message.text && (
            <div className="prose prose-invert prose-sm max-w-none
              [&_p]:mb-3 [&_p:last-child]:mb-0
              [&_strong]:text-[#F4CC04] [&_strong]:font-bold
              [&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-3 [&_h1]:leading-tight
              [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-3.5 [&_h2]:mb-2.5 [&_h2]:leading-tight
              [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-2 [&_h3]:leading-tight
              [&_h4]:text-base [&_h4]:font-semibold [&_h4]:mt-2.5 [&_h4]:mb-1.5 [&_h4]:leading-tight
              [&_h5]:text-sm [&_h5]:font-semibold [&_h5]:mt-2 [&_h5]:mb-1.5 [&_h5]:leading-tight
              [&_h6]:text-sm [&_h6]:font-semibold [&_h6]:mt-2 [&_h6]:mb-1.5 [&_h6]:leading-tight
              [&_ul]:pl-6 [&_ul]:mb-3 [&_ul]:list-disc
              [&_ol]:pl-6 [&_ol]:mb-3 [&_ol]:list-decimal
              [&_li]:mb-1
              [&_pre]:bg-black/20 [&_pre]:p-3 [&_pre]:rounded-md [&_pre]:overflow-x-auto [&_pre]:my-3
              [&_code]:font-mono [&_code]:bg-black/20 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm
              [&_blockquote]:border-l-4 [&_blockquote]:border-[#F4CC04] [&_blockquote]:pl-3 [&_blockquote]:ml-0 [&_blockquote]:text-white/80
              [&_table]:border-collapse [&_table]:w-full [&_table]:my-4
              [&_th]:border [&_th]:border-white/20 [&_th]:p-2 [&_th]:text-left [&_th]:bg-black/20
              [&_td]:border [&_td]:border-white/20 [&_td]:p-2 [&_td]:text-left
              [&_hr]:border-0 [&_hr]:border-t [&_hr]:border-white/20 [&_hr]:my-4
            ">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: CustomLink,
                  strong: ({ children }) => <strong className="text-[#F4CC04] font-bold">{children}</strong>,
                }}
              >
                {message.text}
              </ReactMarkdown>
            </div>
          )}

          {/* Artifact Card DEPOIS do texto */}
          {message.artifact && onViewArtifact && (
            <ArtifactCard
              artifact={message.artifact}
              onView={() => onViewArtifact(message.artifact!)}
            />
          )}

          {/* Artifact em streaming */}
          {message.streamingArtifact && !message.artifact && (
            <ArtifactStreamingBox
              title={message.streamingArtifact.title}
              type={message.streamingArtifact.type}
              content={message.streamingArtifact.content}
              language={message.streamingArtifact.language}
            />
          )}
        </div>

        {/* Botão de copiar */}
        {isAI && message.text && (
          <div className="flex justify-start mt-2 opacity-0 hover:opacity-100 transition-opacity group-hover:opacity-100">
            <CopyButton
              text={(() => {
                // Se tem artifact, concatena texto + conteúdo do artifact
                if (message.artifact) {
                  return `${message.text}\n\n--- ${message.artifact.title} ---\n\n${message.artifact.content}`;
                }
                // Se não tem artifact, usa rawText (texto original completo) ou text como fallback
                return message.rawText || message.text;
              })()}
            />
          </div>
        )}
      </div>
    </div>
  );
};
